import { FieldWrapperProps } from '@dixa/lib-shared-habla';
import pick from 'lodash/pick';
import { ComponentPropsWithoutRef, ElementType } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  UseControllerReturn,
} from 'react-hook-form';

import { getIsHookFormOptionRequired } from '../getIsHookFormOptionRequired';
import { useHookForm } from '../useHookForm/useHookForm';

import { HookFormFieldWrappedBaseProps } from './useHookFormFieldWrapper.types';
import { hookFormFieldWrapperProps } from './utils/hookFormFieldWrapperProps';

type UseHookFormFieldWrapperResult<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  /** The {@link useController} result. */
  readonly controller: UseControllerReturn<TFieldValues, TName>;
  /** The {@link useController} props. */
  readonly controllerProps: UseControllerProps<TFieldValues, TName>;
  /** The {@link FieldWrapper} props. */
  readonly fieldWrapperProps: FieldWrapperProps;
};

/**
 * Represents a hook for creating a React Hook Form based component wrapped in
 * {@link FieldWrapper}.
 */
export function useHookFormFieldWrapper<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TFieldComponentProps extends ComponentPropsWithoutRef<ElementType> = never,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    TFieldComponentProps
  >,
): UseHookFormFieldWrapperResult<TFieldValues, TName> {
  const { controller, controllerProps } = useHookForm(props);
  const fieldWrapperProps: FieldWrapperProps = {
    ...pick(props, hookFormFieldWrapperProps),
    required: getIsHookFormOptionRequired(controllerProps.rules?.required),
    meta: {
      touched: controller.fieldState.isTouched,
      error: controller.fieldState.error?.message,
      success: props.meta?.success,
      warning: props.meta?.warning,
    },
    labelFor: controllerProps.name,
    noLabel: props.noLabel,
    noAlert: props.noAlert,
  };

  return {
    controller,
    controllerProps,
    fieldWrapperProps,
  };
}
